import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { apis } from '../config/api';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { AUTH_REFRESH_TOKEN_KEY, AUTH_TOKEN_KEY } from '../config';
import { Params, Router } from '@angular/router';
import { USER_ROLES } from '../config/user-roles';
import { PermissionService } from './permission.service';
import { CerateSchool, SignupUser, User } from '../models';

interface IAuthTokens {
  access: { token: string }, refresh: { token: string }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  http = inject(HttpClient);
  userSig = signal<User | undefined | null>(undefined);
  router = inject(Router);
  permissionSvs = inject(PermissionService);

  isLoggedIn() {
    return !!this.userSig();
  }

  isOnboardingRequired() {
    return !!(this.userSig() && this.userSig()?.role === USER_ROLES.UNASSIGNED);
  }

  is(role: number) {
    return this.userSig()?.role === role;
  }

  setUserAndPermission(user: User | null, permissions?: string[]) {
    this.userSig.set(user);
    if (permissions) {
      // Always include 'public' so any authenticated user can access
      // sidebar items and routes guarded with PERMISSIONS.PUBLIC.
      const withPublic = permissions.includes('public') ? permissions : ['public', ...permissions];
      this.permissionSvs.loadPermissions(withPublic);
    }
  }

  async login(credentials: { email: string; password: string }) {
    try {
      const res = await lastValueFrom(
        this.http.post<{
          user: User;
          tokens: IAuthTokens;
          permissions: string[]
        }>(apis.login, credentials)
      );
      const { user, permissions } = res;
      this.saveTokens(res.tokens)
      this.setUserAndPermission(user, permissions);
      return user;
    } catch (err) {
      this.setUserAndPermission(null, []);
      throw err;
    }
  }

  async signup(userBody: SignupUser) {
    try {
      const res = await lastValueFrom(
        this.http.post<{
          user: User;
          createdUser: User,
          tokens: IAuthTokens;
          permissions: string[]
        }>(apis.register, userBody)
      );

      const { user = null, tokens, permissions } = res;

      this.saveTokens(tokens);
      this.setUserAndPermission(user, permissions);
      return user;
    } catch (e) {
      throw e;
    }
  }

  logout() {
    this.removeTokens()
    this.setUserAndPermission(null, []);
    this.router.navigateByUrl('/auth')
  }

  async getMe() {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    if (!token) return;

    try {
      const { user = null, permissions } = await lastValueFrom(
        this.http.get<{ user: User, permissions: string[] }>(apis.getMe)
      );
      this.setUserAndPermission(user, permissions);
    } catch (e) {
      console.log(e);
      // localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  }

  forgotPassword(email: string) {
    return lastValueFrom(this.http.post(apis.forgot, { email }));
  }

  resetPassword(password: string, token: string) {
    return lastValueFrom(
      this.http.post(
        apis.resetPassword,
        { password },
        { params: { token } }
      )
    );
  }

  register(params: Params): Observable<any> {
    return this.http.post(apis.register + '/register', params);
  }

  createInstructor(instructorData: Partial<import('../interfaces').InstructorInterface>): Observable<any> {
    return this.http.post(apis.schoolInstructors, instructorData);
  }

  createSchool(schoolData: CerateSchool): Observable<any> {
    return this.http.post(apis.schools, schoolData);
  }

  updateUser(userId: string, userData: Partial<User>): Observable<any> {
    return this.http.patch(`${apis.users}/${userId}`, userData);
  }

  redirectBasedOnRole() {
    if (this.isOnboardingRequired()) {
      this.router.navigateByUrl('/auth/onboarding');
      return;
    }

    this.router.navigateByUrl('/');
  }

  refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this.http.post(apis.refreshToken, { refreshToken });
  }

  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  }



  saveTokens(tokens: IAuthTokens) {
    const {
      access: { token },
      refresh: { token: refreshToken }
    } = tokens;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken)
  }

  removeTokens() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)

  }
}
